export const APP_MODES = {
  FLEX2: "flex2",
  AFMS: "afms",
  LCP: "lcp",
  ENERGY: "energy",
  TRAINING: "training",
  INSPECTIONS: "inspections",
};

export const AFMS_ROLES = new Set(["RECYCLING", "Waste Oil"]);

// Name of the org-system that grants AFMS mode access (catalog row seeded in
// AddOrganizationSystems migration). When an org has this in its
// OrganizationSystemMappings, every user in that org can switch into AFMS.
export const AFMS_SYSTEM_NAME = "Environmental";

// Same role as AFMS_SYSTEM_NAME for the LiftConnect Pro system. Used by the
// per-asset tab gate on /assets/edit so the LCP tab only shows for assets
// tagged with this catalog name AND while the user is in LCP app-mode.
export const LCP_SYSTEM_NAME = "LiftConnect Pro";

// VendorTypes.name that marks an org as a seller of the Flex2 platform
// itself — the SubscriptionPlans catalog rather than physical services.
// Carried on userDetails.organizationVendorTypes; gates the Fixed Ops
// "Software Catalog" group and the quote builder's "+ Add Subscription"
// button. The F2 org (subdomain f2) carries it today; a reseller org would
// get the same row ticked in Organization Admin → Vendor → Vendor Types.
export const SOFTWARE_VENDOR_TYPE = "Software & Subscriptions";

// Energy system catalog name (Plan K Phase 3). Mirrors the OrganizationSystems
// row seeded for "Energy" — when an org has this mapped, users in that org
// get the "energy" app-mode and can switch into the EnergyPortal sidebar.
export const ENERGY_SYSTEM_NAME = "Energy";

// Training system catalog name. Mirrors the OrganizationSystems row seeded
// for "Training". The portal itself lives in training-webapp, so this app
// only needs the name, the label and the route to hand users over.
export const TRAINING_SYSTEM_NAME = "Training";

// The backoffice org is our admin surface — it always lives in the flex2
// sidebar regardless of which OrganizationSystems are mapped to it. Behind
// a named constant so the literal `1` is one find-and-replace away if it
// ever moves. The server enforces the same rule in
// `Repository/UserRepository.cs`; the front-end check is belt-and-suspenders
// for any cached / stale userDetails payload.
export const BACKOFFICE_ORG_ID = 1;

// User-facing labels for the picker. Keys are app-mode strings (the
// canonical persistence format on UserProfiles.preferredAppMode); values
// are the human-readable system names users actually recognise. Unknown
// keys fall back to the raw key so new modes degrade gracefully.
export const SYSTEM_DISPLAY_NAMES = {
  [APP_MODES.FLEX2]: "Fixed Operations",
  [APP_MODES.AFMS]: "Environmental",
  [APP_MODES.LCP]: "LiftConnect Pro",
  [APP_MODES.ENERGY]: "Energy",
  [APP_MODES.TRAINING]: "Training",
  [APP_MODES.INSPECTIONS]: "Inspections",
};

// True when we should fire the "Select your default system" picker on
// post-login landing. Three gates, all must hold:
//   - user is NOT in the backoffice org (org 1 is always flex2)
//   - user has no preferredAppMode saved yet (NULL on UserProfiles)
//   - user has more than one mode available to choose from
export const needsDefaultSystemPick = (userDetails) => {
  if (!userDetails) return false;
  if (userDetails.organizationId === BACKOFFICE_ORG_ID) return false;
  if (userDetails.preferredAppMode) return false;
  const modes = userDetails.availableAppModes ?? [];
  return modes.length > 1;
};

// Normalizes the two coexisting shapes of `state.auth.user`:
//
//   - Post-loginUserSuccess: state.auth.user IS the inner UserDetailsDto
//     (organizationId, role, effectiveAppMode at the top level).
//   - Post-getUserSuccess: state.auth.user is an outer wrapper that has
//     the inner UserDetailsDto nested at `.userDetails`.
//
// This is a known inconsistency in the codebase — see `src/api/index.js:36`
// which uses the same `?? user.userDetails` fallback. Until the reducers
// are unified, every read that wants the inner shape must go through this
// helper. Returns null when no userDetails are resolvable.
export const selectUserDetails = (authUser) => {
  if (!authUser) return null;
  // If the object already has a nested userDetails, prefer that (post-getUser
  // shape). Otherwise treat the object itself as the inner DTO (post-login
  // shape).
  if (authUser.userDetails) return authUser.userDetails;
  if (typeof authUser.organizationId !== "undefined") return authUser;
  return null;
};

// Sidebar variants the app can render. Single source of truth so App.js
// and tests agree on the symbol set.
export const SIDEBAR_VARIANTS = {
  MAIN: "main",
  ENVIRONMENTAL_PORTAL: "environmentalPortal",
  LCP_PORTAL: "lcpPortal",
  ENERGY_PORTAL: "energyPortal",
  FIXED_OPS_PORTAL: "fixedOpsPortal",
};

// The ONE sidebar decision. Run once at the top of the app off userDetails;
// the page the user is on does not get a vote.
//
//   - logged out / no userDetails       → null (no sidebar; e.g. login page)
//   - organizationId === 1 (backoffice) → "main" (the old Flex2 sidebar)
//   - effectiveAppMode === "afms"       → "environmentalPortal" (green-leaf)
//   - effectiveAppMode === "lcp"        → "lcpPortal" (red car-on-hoist)
//   - effectiveAppMode === "energy"     → "energyPortal" (orange)
//   - effectiveAppMode === "flex2"      → "fixedOpsPortal" (industrial-blue
//                                          wrench sidebar for orgs running
//                                          the Fixed Operations system —
//                                          non-backoffice flex2 users)
//   - anything else                     → "main" (last-resort fallback)
//
// Pure function on purpose so it's trivially testable.
export const pickSidebarVariant = (userDetails) => {
  if (!userDetails) return null;
  if (userDetails.organizationId === BACKOFFICE_ORG_ID) return SIDEBAR_VARIANTS.MAIN;
  if (userDetails.effectiveAppMode === APP_MODES.AFMS) return SIDEBAR_VARIANTS.ENVIRONMENTAL_PORTAL;
  if (userDetails.effectiveAppMode === APP_MODES.LCP) return SIDEBAR_VARIANTS.LCP_PORTAL;
  if (userDetails.effectiveAppMode === APP_MODES.ENERGY) return SIDEBAR_VARIANTS.ENERGY_PORTAL;
  // FLEX2 mode = user picked Fixed Operations from the system
  // picker (only available to authorized users). Land in the
  // FixedOps shell. Unauthed bare-vanity-URL hits never reach
  // here because App.js short-circuits /login + auth paths.
  if (userDetails.effectiveAppMode === APP_MODES.FLEX2) return SIDEBAR_VARIANTS.FIXED_OPS_PORTAL;
  return SIDEBAR_VARIANTS.MAIN;
};

// Lift Connect Pro is a build-time product selection, not a role-derived one
// like AFMS. The dedicated liftconnectpro.com bundle sets REACT_APP_PRODUCT=LCP
// in .env.lcp / .env.lcp-dev; everything else falls back to role-based routing.
export const BUILD_PRODUCT = (process.env.REACT_APP_PRODUCT || "").toUpperCase();
export const IS_LCP_BUILD = BUILD_PRODUCT === "LCP";

export const DEFAULT_ROUTES = {
  [APP_MODES.FLEX2]: "/fixedops/control-center",
  [APP_MODES.AFMS]: "/afms/overview",
  [APP_MODES.LCP]: "/lcp/overview",
  [APP_MODES.ENERGY]: "/energy/control-center",
  [APP_MODES.TRAINING]: "/training/overview",
  [APP_MODES.INSPECTIONS]: "/inspections/overview",
};

// Pre-CollapsePrimaryRoles (2026-06-11) "vendor identity" was a USER
// primary role string. After the collapse it's an ORG-level flag
// (userDetails.isVendorOrganization). VENDOR_ROLES is retained as an
// empty Set so legacy consumers that haven't been migrated yet keep
// compiling and continue to gate `false`. Delete callers + this Set
// as part of Slice 4/5 of the role refactor.
export const VENDOR_ROLES = new Set();

export const RECYCLING_DEVICE_TYPES = new Set(["TA01", "LVL01"]);

export const MAINTENANCE_DEVICE_TYPES = new Set([
  "CP01", "CP02", "CCTL01",
  "DL01", "DL02", "DL03", "DL04",
  "DR01", "DR02",
  "EN01", "EN02", "EN03",
  "QD01", "QD02", "QD03",
  "DP01", "DP02",
  "AC01", "MF01",
]);

/**
 * Returns the set of allowed device types for a vendor user. After
 * CollapsePrimaryRoles (2026-06-11) the role-string discriminator
 * (RECYCLING_VENDOR / MAINTENANCE_VENDOR) is gone — vendor type is
 * now an org-level property carried in
 * `userDetails.organizationVendorTypes` (an array of vendor type names
 * the org carries in OrganizationVendorTypes). Returns the device-type
 * union of all the org's vendor types; null when the user/org has no
 * vendor identity (no filtering — show every device).
 */
export const getVendorDeviceTypes = (userDetails) => {
  // Back-compat: a bare role-string argument now resolves to "no filter".
  if (typeof userDetails === "string" || userDetails == null) return null;
  const types = userDetails?.organizationVendorTypes;
  if (!Array.isArray(types) || types.length === 0) return null;
  const allowed = new Set();
  for (const t of types) {
    const n = t?.toLowerCase();
    if (n === "recycling") {
      RECYCLING_DEVICE_TYPES.forEach((x) => allowed.add(x));
    }
    // Maintenance VendorType isn't in the catalog yet; once seeded,
    // add `else if (n === "maintenance") ...`.
  }
  return allowed.size === 0 ? null : Array.from(allowed);
};

// Returns the list of app modes the user can switch into. Pulled straight
// from the userDetails.availableAppModes server payload (single source of
// truth), with a defensive fallback to legacy role-based logic when the
// payload doesn't include it (cached pre-upgrade login).
export const getAvailableAppModes = (userDetails) => {
  if (Array.isArray(userDetails?.availableAppModes) && userDetails.availableAppModes.length > 0) {
    return userDetails.availableAppModes;
  }
  // Fallback for pre-CollapsePrimaryRoles cached payloads. After
  // 2026-06-11 the server always populates `availableAppModes` from
  // the org's `OrganizationSystemMappings`; the role-string heuristic
  // below only kicks in for tokens minted against the old DTO.
  const modes = new Set([APP_MODES.FLEX2]);
  if (AFMS_ROLES.has(userDetails?.role)) modes.add(APP_MODES.AFMS);
  if (userDetails?.role?.toUpperCase() === "SUPER_ADMIN") modes.add(APP_MODES.AFMS);
  return Array.from(modes);
};

// Resolves the user's app mode. Reads the server-computed effectiveAppMode
// when available; otherwise falls back to the original role-only logic so
// pre-upgrade clients don't break.
export const getAppMode = (userDetails) => {
  // Back-compat: if a caller still passes a bare string, treat it as the role.
  if (typeof userDetails === "string" || userDetails == null) {
    const roleName = userDetails;
    if (roleName === "SUPER_ADMIN") return APP_MODES.FLEX2;
    if (IS_LCP_BUILD) return APP_MODES.LCP;
    if (AFMS_ROLES.has(roleName)) return APP_MODES.AFMS;
    return APP_MODES.FLEX2;
  }

  // Backoffice short-circuit — defends against a stale `effectiveAppMode`
  // in a cached payload. The server enforces the same rule.
  if (userDetails.organizationId === BACKOFFICE_ORG_ID) return APP_MODES.FLEX2;

  if (userDetails.effectiveAppMode) return userDetails.effectiveAppMode;

  // Defensive fallback for pre-upgrade payloads.
  const roleName = userDetails.role;
  if (roleName === "SUPER_ADMIN") return APP_MODES.FLEX2;
  if (IS_LCP_BUILD) return APP_MODES.LCP;
  if (AFMS_ROLES.has(roleName)) return APP_MODES.AFMS;
  return APP_MODES.FLEX2;
};

// Maps an app-mode key to the OrganizationSystem name used as the
// lookup key in userDetails.systemDefaultRoutes (which the server keys by
// system *name*, not mode). Adding a new system means one row here +
// one OrganizationSystems catalog row server-side.
export const systemNameForAppMode = (appMode) => {
  switch (appMode) {
    case APP_MODES.AFMS:
      return "Environmental";
    case APP_MODES.FLEX2:
      return "Fixed Operations";
    case APP_MODES.LCP:
      return "LiftConnect Pro";
    case APP_MODES.ENERGY:
      return "Energy";
    case APP_MODES.TRAINING:
      return "Training";
    case APP_MODES.INSPECTIONS:
      return "Inspections";
    default:
      return null;
  }
};

// Post-login landing path. Resolution order (top wins):
//
//   1. User's pinned home for the current system
//      (userDetails.systemDefaultRoutes[systemName]) — the "Set as my
//      home" button writes this. Always wins so a user who has pinned
//      /afms/dashboard actually lands there, even if their org is also
//      flagged as a vendor.
//   2. Explicit vendor *role* bypass — RECYCLING_VENDOR / VENDOR /
//      MAINTENANCE_VENDOR land on /vendor/dashboard. These are
//      assigned roles, not org flags.
//   3. DEFAULT_ROUTES[appMode] — Environmental → /afms/overview,
//      flex2 → /user/devices, lcp → /lcp/overview.
//
// Note: the historic `isVendorOrganization === true → /vendor/dashboard`
// bypass was removed. It clobbered every pinned home for vendor orgs
// and was the cause of the "click logo, land on wrong page" bug. Vendor
// orgs in Environmental still see the /vendor/dashboard route via the
// Legacy collapse if they want it; they just don't auto-land there.
export const getDefaultRoute = (userDetailsOrRole, isVendorOrgArg = false) => {
  // Back-compat: accept old (roleName, isVendorOrg) signature.
  const userDetails = typeof userDetailsOrRole === "string" || userDetailsOrRole == null
    ? { role: userDetailsOrRole, isVendorOrganization: isVendorOrgArg }
    : userDetailsOrRole;

  // energy-webapp is a single-system app: every login lands in the Energy
  // portal regardless of org (including backoffice org 1) or
  // effectiveAppMode. Only the user's pinned Energy home ("Set as my
  // home" → systemDefaultRoutes["Energy"]) overrides the default.
  const pinned = userDetails?.systemDefaultRoutes?.[ENERGY_SYSTEM_NAME];
  if (pinned) return pinned;

  return DEFAULT_ROUTES[APP_MODES.ENERGY];
};
